/**
 * AgreementClientStep - Client selection for agreements
 *
 * Search existing clients or add a new one
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getFirestore, collection, onSnapshot } from 'firebase/firestore';
import { useAuth } from '../../../contexts/AuthContext';
import { FlowInput, FlowButton, StepContainer, FlowNavigation } from '../FlowComponents';

const AgreementClientStep = ({ data, updateData, goNext, goBack }) => {
  const { currentUser } = useAuth();
  const [clients, setClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newClient, setNewClient] = useState({ name: '', email: '', company: '' });
  const [isLoading, setIsLoading] = useState(true);

  // Fetch clients from Firestore
  useEffect(() => {
    if (!currentUser) return;

    const firestore = getFirestore();
    const clientsRef = collection(firestore, 'users', currentUser.uid, 'clients');

    const unsubscribe = onSnapshot(clientsRef, (snapshot) => {
      const clientsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setClients(clientsData);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [currentUser]);

  // Filter clients based on search
  const filteredClients = clients.filter(client => {
    const query = searchQuery.toLowerCase();
    return (
      client.name?.toLowerCase().includes(query) ||
      client.email?.toLowerCase().includes(query) ||
      client.company?.toLowerCase().includes(query)
    );
  });

  // Get recent clients (last 3)
  const recentClients = clients
    .sort((a, b) => {
      const dateA = a.lastAgreementDate?.toDate?.() || a.createdAt?.toDate?.() || new Date(0);
      const dateB = b.lastAgreementDate?.toDate?.() || b.createdAt?.toDate?.() || new Date(0);
      return dateB - dateA;
    })
    .slice(0, 3);

  const handleClientSelect = (client) => {
    // Pass data directly to goNext to avoid race condition with updateData
    goNext({
      client: {
        id: client.id,
        name: client.name,
        email: client.email,
        company: client.company,
      },
      clientName: client.name,
      clientEmail: client.email,
    });
  };

  const handleNewClientSubmit = () => {
    if (!newClient.name || !newClient.email) return;

    // Pass data directly to goNext to avoid race condition with updateData
    goNext({
      client: {
        id: null, // New client, no ID yet
        name: newClient.name,
        email: newClient.email,
        company: newClient.company,
        isNew: true,
      },
      clientName: newClient.name,
      clientEmail: newClient.email,
    });
  };

  const canContinueNewClient = newClient.name && newClient.email;

  return (
    <StepContainer>
      <div className="max-w-xl mx-auto">
        <AnimatePresence mode="wait">
          {!isAddingNew ? (
            <motion.div
              key="select"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="space-y-6"
            >
              {/* Search */}
              <div className="relative">
                <svg
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-[#666666]"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Search clients by name, email, or company..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 bg-gray-50 dark:bg-[#111111] border border-gray-200 dark:border-[#2A2A2A] rounded-2xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-[#666666] focus:outline-none focus:ring-2 focus:ring-[#FFB27D]/20 focus:border-brand"
                />
              </div>

              {/* Recent clients (only when not searching) */}
              {!searchQuery && recentClients.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-gray-500 dark:text-[#666666] uppercase tracking-wide">
                    Recent Clients
                  </h3>
                  <div className="space-y-2">
                    {recentClients.map((client) => (
                      <motion.button
                        key={client.id}
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.99 }}
                        onClick={() => handleClientSelect(client)}
                        className={`w-full p-4 bg-white dark:bg-[#0A0A0A] border rounded-2xl text-left transition-all ${
                          data.client?.id === client.id
                            ? 'border-brand ring-2 ring-[#FFB27D]/20'
                            : 'border-gray-200 dark:border-[#1A1A1A] hover:border-gray-300 dark:hover:border-[#2A2A2A]'
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FFB27D] to-[#FF8F3F] flex items-center justify-center text-black font-medium">
                            {client.name?.charAt(0).toUpperCase() || '?'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900 dark:text-white truncate">
                              {client.name}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-[#666666] truncate">
                              {client.email}
                            </div>
                          </div>
                          {client.company && (
                            <div className="text-xs text-gray-400 dark:text-[#666666] bg-gray-100 dark:bg-[#1A1A1A] px-2 py-1 rounded">
                              {client.company}
                            </div>
                          )}
                        </div>
                      </motion.button>
                    ))}
                  </div>
                </div>
              )}

              {/* Search results */}
              {searchQuery && (
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-gray-500 dark:text-[#666666] uppercase tracking-wide">
                    {filteredClients.length} Results
                  </h3>
                  {filteredClients.length > 0 ? (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {filteredClients.map((client) => (
                        <motion.button
                          key={client.id}
                          whileHover={{ scale: 1.01 }}
                          whileTap={{ scale: 0.99 }}
                          onClick={() => handleClientSelect(client)}
                          className="w-full p-4 bg-white dark:bg-[#0A0A0A] border border-gray-200 dark:border-[#1A1A1A] hover:border-gray-300 dark:hover:border-[#2A2A2A] rounded-2xl text-left transition-all"
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FFB27D] to-[#FF8F3F] flex items-center justify-center text-black font-medium">
                              {client.name?.charAt(0).toUpperCase() || '?'}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-gray-900 dark:text-white truncate">
                                {client.name}
                              </div>
                              <div className="text-sm text-gray-500 dark:text-[#666666] truncate">
                                {client.email}
                              </div>
                            </div>
                          </div>
                        </motion.button>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500 dark:text-[#666666]">
                      <p>No clients found matching "{searchQuery}"</p>
                      <button
                        onClick={() => {
                          setIsAddingNew(true);
                          setNewClient({ ...newClient, name: searchQuery });
                        }}
                        className="mt-2 text-brand hover:underline"
                      >
                        Add "{searchQuery}" as a new client
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Add new client button */}
              <div className="pt-4 border-t border-gray-200 dark:border-[#1A1A1A]">
                <button
                  onClick={() => setIsAddingNew(true)}
                  className="w-full p-4 border-2 border-dashed border-gray-200 dark:border-[#2A2A2A] hover:border-brand dark:hover:border-brand rounded-2xl text-gray-500 dark:text-[#666666] hover:text-brand transition-all flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Add New Client
                </button>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="add-new"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="flex items-center gap-4 mb-6">
                <button
                  onClick={() => setIsAddingNew(false)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-[#1A1A1A] rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5 text-gray-500 dark:text-[#666666]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                </button>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Add New Client
                </h3>
              </div>

              <FlowInput
                label="Client Name"
                placeholder="e.g., John Smith"
                value={newClient.name}
                onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                required
              />

              <FlowInput
                label="Email Address"
                type="email"
                placeholder="e.g., john@example.com"
                value={newClient.email}
                onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                required
                hint="They'll receive the agreement at this email"
              />

              <FlowInput
                label="Company (Optional)"
                placeholder="e.g., Smith Photography"
                value={newClient.company}
                onChange={(e) => setNewClient({ ...newClient, company: e.target.value })}
              />

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setIsAddingNew(false)}
                  className="flex-1 py-3 border border-gray-200 dark:border-[#2A2A2A] text-gray-700 dark:text-[#999999] rounded-2xl hover:bg-gray-50 dark:hover:bg-[#111111] transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleNewClientSubmit}
                  disabled={!canContinueNewClient}
                  className={`flex-1 py-3 rounded-2xl font-medium transition-colors ${
                    canContinueNewClient
                      ? 'bg-brand hover:bg-brand-hover text-black'
                      : 'bg-gray-100 dark:bg-[#1A1A1A] text-gray-400 dark:text-[#666666] cursor-not-allowed'
                  }`}
                >
                  Continue with Client
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Navigation (only when not adding new) */}
        {!isAddingNew && (
          <div className="mt-8">
            <FlowNavigation
              onBack={goBack}
              disabled={true}
            />
          </div>
        )}
      </div>
    </StepContainer>
  );
};

export default AgreementClientStep;
